import { type AnyAction, combineReducers } from 'redux';

import type { DashboardVideo } from 'js/src/client';
import {
    addViews,
    clearVideos,
    deleteVideoFromStore,
    deleteVideoFromView,
    removeView,
    setActiveView,
    setBulkActionSpinner,
    setLoadingDashboard,
    setVideoSearchTerm,
    storeLoadedUpToPage,
    storeMoreVideosToLoad,
    storeRenderEstimates,
    storeVideos,
    storeVideosFilterByAspectRatio,
    storeVideosFilterByNarrativeThread,
    storeVideosFilterByStatus,
    storeVideosFilterByTemplateId,
    storeVideosFilterByUserId,
    storeVideosSortBy,
    updateVideoProperty,
    updateViewMetadata,
} from 'js/src/data/dashboard/dashboard-actions';
import type { RenderEstimateStatus, View } from 'js/src/data/dashboard/dashboard-types';
import {
    ALL_VIDEOS_VIEW_ID,
    CREATE_VIEW_ID,
    INITIAL_VIEWS,
    VIDEOS_KEY,
    VIEWS_ORM_PREFIX,
} from 'js/src/data/dashboard/dashboard-types';
import type { StoreORMInterface } from 'js/src/data/store-orm';
import StoreORM from 'js/src/data/store-orm';
import Flags from 'js/src/libs/flags';

export function loadingDashboard(state = true, action: AnyAction) {
    if (storeVideos.match(action)) {
        return false;
    }
    if (setLoadingDashboard.match(action)) {
        return action.payload;
    }
    return state;
}

export function estimatedRenderDurations(
    state = StoreORM.newFromIdPrefix<RenderEstimateStatus>('renderEstimates').data,
    action: AnyAction,
) {
    if (storeRenderEstimates.match(action)) {
        // replace old data
        const orm = StoreORM.newFromIdPrefix<RenderEstimateStatus>('renderEstimates');
        const videoIdKeys = Object.keys(action.payload).map(Number);
        videoIdKeys.forEach((videoId) => {
            orm.pushJSItem(
                {
                    videoId,
                    ...action.payload[videoId],
                },
                'videoId',
            );
        });
        return orm.data;
    }
    return state;
}

const DEFAULT_VIEW_ID = Flags.canSeeHomeTab() ? CREATE_VIEW_ID : ALL_VIDEOS_VIEW_ID;

export function activeView(state = DEFAULT_VIEW_ID, action: AnyAction) {
    if (setActiveView.match(action)) {
        return action.payload.view;
    }
    return state;
}

export const _initialViewOrm = (() => {
    const viewOrm = StoreORM.newFromIdPrefix<View>(VIEWS_ORM_PREFIX);
    INITIAL_VIEWS.forEach((initialView) => {
        viewOrm.pushJSItem(initialView, 'id');
    });
    return viewOrm.data;
})();

export function views(state: StoreORMInterface<View> = _initialViewOrm, action: AnyAction) {
    const viewOrm = new StoreORM<View>(state);

    /* ADD_VIEWS */
    if (addViews.match(action)) {
        action.payload.views.forEach((view) => {
            viewOrm.pushItem(view, 'id');
        });
        return viewOrm.data;
    }

    /* REMOVE_VIEW */
    if (removeView.match(action)) {
        viewOrm.delete([action.payload.view]);
        return viewOrm.data;
    }

    /* UPDATE_VIEW_METADATA */
    if (updateViewMetadata.match(action)) {
        if (action.payload.name) {
            viewOrm.updateById(action.payload.view, {
                value: action.payload.name,
                key: ['name'],
            });
        }
        if (action.payload.color) {
            viewOrm.updateById(action.payload.view, {
                value: action.payload.color,
                key: ['color'],
            });
        }
        if (action.payload.icon) {
            viewOrm.updateById(action.payload.view, {
                value: action.payload.icon,
                key: ['icon'],
            });
        }
        return viewOrm.data;
    }

    /* STORE_VIDEOS */
    if (storeVideos.match(action)) {
        const viewDetails = viewOrm.getById(action.payload.view);
        const videosOrm = new StoreORM<DashboardVideo>(viewDetails.get(VIDEOS_KEY));
        action.payload.videos.forEach((video) => {
            if (action.payload.addToEnd) {
                videosOrm.pushItem(video, 'id');
            } else {
                videosOrm.pushItemToFront(video, 'id');
            }
        });
        viewOrm.updateById(action.payload.view, {
            key: [VIDEOS_KEY],
            value: videosOrm.data,
        });
        return viewOrm.data;
    }

    /* STORE_MORE_VIDEOS_TO_LOAD */
    if (storeMoreVideosToLoad.match(action)) {
        viewOrm.updateById(action.payload.view, {
            key: ['moreVideosToLoad'],
            value: action.payload.hasMoreVideos,
        });
        return viewOrm.data;
    }

    /* STORE_LOADED_PAGE_NUMBER */
    if (storeLoadedUpToPage.match(action)) {
        viewOrm.updateById(action.payload.view, {
            key: ['loadedUpToPage'],
            value: action.payload.loadedUpToPage,
        });
        return viewOrm.data;
    }

    /* CLEAR_VIDEOS */
    if (clearVideos.match(action)) {
        const viewDetails = viewOrm.getById(action.payload.view);
        const videosOrm = new StoreORM<DashboardVideo>(viewDetails.get(VIDEOS_KEY));
        videosOrm.delete(videosOrm.getAllIds());

        viewOrm.updateById(action.payload.view, {
            key: [VIDEOS_KEY],
            value: videosOrm.data,
        });
        viewOrm.updateById(action.payload.view, {
            key: ['loadedUpToPage'],
            value: 0,
        });
        viewOrm.updateById(action.payload.view, {
            key: ['moreVideosToLoad'],
            value: true,
        });
        return viewOrm.data;
    }

    /* STORE_VIDEOS_SORT_BY */
    if (storeVideosSortBy.match(action)) {
        viewOrm.updateById(action.payload.view, {
            key: ['videosSortBy'],
            value: action.payload.videosSortBy,
        });
        return viewOrm.data;
    }

    /* STORE_VIDEOS_FILTER_BY_USER_ID */
    if (storeVideosFilterByUserId.match(action)) {
        viewOrm.updateById(action.payload.view, {
            key: ['filterByUserId'],
            value: action.payload.userId,
        });
        return viewOrm.data;
    }

    /* STORE_VIDEOS_FILTER_BY_NARRATIVE_THREAD */
    if (storeVideosFilterByNarrativeThread.match(action)) {
        viewOrm.updateById(action.payload.view, {
            key: ['filterByNarrativeThread'],
            value: action.payload.narrativeThread,
        });
        return viewOrm.data;
    }

    /* STORE_VIDEOS_FILTER_BY_ASPECT_RATIO */
    if (storeVideosFilterByAspectRatio.match(action)) {
        viewOrm.updateById(action.payload.view, {
            key: ['filterByAspectRatio'],
            value: action.payload.aspectRatio,
        });
        return viewOrm.data;
    }

    /* STORE_VIDEOS_FILTER_BY_STATUS */
    if (storeVideosFilterByStatus.match(action)) {
        viewOrm.updateById(action.payload.view, {
            key: ['filterByStatus'],
            value: action.payload.status,
        });
        return viewOrm.data;
    }

    /* STORE_VIDEOS_FILTER_BY_TEMPLATE_ID */
    if (storeVideosFilterByTemplateId.match(action)) {
        viewOrm.updateById(action.payload.view, {
            key: ['filterByTemplateId'],
            value: action.payload.templateId,
        });
        return viewOrm.data;
    }

    /* UPDATE_VIDEO_PROPERTY */
    if (updateVideoProperty.match(action)) {
        // look in each view for the video, updating when necessary
        viewOrm.forEach((viewDetails) => {
            const videosOrm = new StoreORM<DashboardVideo>(viewDetails.get(VIDEOS_KEY));
            if (videosOrm.contains(action.payload.videoId)) {
                videosOrm.updateById(action.payload.videoId, {
                    key: [action.payload.property],
                    value: action.payload.newValue,
                });
                viewOrm.updateById(viewDetails.get('id'), {
                    key: [VIDEOS_KEY],
                    value: videosOrm.data,
                });
            }
        });
        return viewOrm.data;
    }

    /* DELETE_VIDEO_FROM_VIEW */
    if (deleteVideoFromView.match(action)) {
        const viewDetails = viewOrm.getById(action.payload.view);
        const videosOrm = new StoreORM<DashboardVideo>(viewDetails.get(VIDEOS_KEY));
        if (videosOrm.contains(action.payload.videoId)) {
            videosOrm.delete([action.payload.videoId]);
            viewOrm.updateById(viewDetails.get('id'), {
                key: [VIDEOS_KEY],
                value: videosOrm.data,
            });
        }
        return viewOrm.data;
    }

    /* DELETE_VIDEO */
    if (deleteVideoFromStore.match(action)) {
        // look in each view for the video, updating when necessary
        viewOrm.forEach((viewDetails) => {
            const videosOrm = new StoreORM<DashboardVideo>(viewDetails.get(VIDEOS_KEY));
            if (videosOrm.contains(action.payload.videoId)) {
                videosOrm.delete([action.payload.videoId]);
                viewOrm.updateById(viewDetails.get('id'), {
                    key: [VIDEOS_KEY],
                    value: videosOrm.data,
                });
            }
        });
        return viewOrm.data;
    }

    return state;
}

export function videoSearchTerm(state = '', action: AnyAction) {
    if (setVideoSearchTerm.match(action)) {
        return action.payload;
    }
    return state;
}

export function bulkActionSpinner(state = false, action: AnyAction) {
    if (setBulkActionSpinner.match(action)) {
        return action.payload;
    }
    return state;
}

export const dashboard = combineReducers({
    activeView,
    views,
    loadingDashboard,
    estimatedRenderDurations,
    videoSearchTerm,
    bulkActionSpinner,
});

export type DashboardState = ReturnType<typeof dashboard>;
